import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getFaQById, updateFaQ } from "services/faqService";
import { CircularProgress } from "@mui/material";
import Dropdown from "components/CustomDropdown/Dropdown";

function EditFAQ() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [formData, setFormData] = useState({
    FaQType: "",
    question: "",
    answer: "",
    isPlanFaQ: false,
  });

  // Fetch FAQ details
  useEffect(() => {
    const fetchFaqDetails = async () => {
      setFetching(true);
      try {
        const response = await getFaQById(id);
        if (response.success) {
          const faq = response.data;
          setFormData({
            FaQType: faq.FaQType,
            question: faq.question,
            answer: faq.answer,
            isPlanFaQ: Boolean(faq.isPlanFaQ),
          });
        }
      } catch (error) {
        console.error("Error fetching FAQ:", error);
        toast.error(error.message || "Failed to fetch FAQ details");
        navigate("/faq");
      } finally {
        setFetching(false);
      }
    };

    fetchFaqDetails();
  }, [id, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.FaQType || !formData.question || !formData.answer) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await updateFaQ(id, formData);
      if (response.success) {
        toast.success(response.message || "FAQ updated successfully!");
        navigate("/faq");
      }
    } catch (error) {
      console.error("Error updating FAQ:", error);
      toast.error(error.message || "Failed to update FAQ");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox
          py={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
              <ArgonTypography variant="h5" fontWeight="medium">
                Edit FAQ
              </ArgonTypography>
              <ArgonButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => navigate("/faq")}
              >
                Back to List
              </ArgonButton>
            </ArgonBox>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* FAQ Type */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    FAQ Type *
                  </ArgonTypography>
                  <Dropdown
                    value={formData.FaQType}
                    options={[
                      { label: "Select FAQ Type", value: "" },
                      { label: "Parent", value: "PARENT" },
                      { label: "Tutor", value: "TUTOR" },
                    ]}
                    onChange={(e) => setFormData({ ...formData, FaQType: e.target.value })}
                    width={300}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={6} display="flex" alignItems="flex-end">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isPlanFaQ}
                        onChange={(e) =>
                          setFormData({ ...formData, isPlanFaQ: e.target.checked })
                        }
                        color="primary"
                      />
                    }
                    label="Show in Plan FAQ"
                  />
                </Grid>
                {/* Question */}
                <Grid item xs={12}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Question *
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    name="question"
                    value={formData.question}
                    onChange={handleChange}
                    placeholder="Enter question"
                    multiline
                    rows={3}
                  />
                </Grid>

                {/* Answer */}
                <Grid item xs={12}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Answer *
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    name="answer"
                    value={formData.answer}
                    onChange={handleChange}
                    placeholder="Enter answer"
                    multiline
                    rows={5}
                  />
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <ArgonButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate("/faq")}
                  disabled={loading}
                >
                  Cancel
                </ArgonButton>
                <ArgonButton type="submit" variant="gradient" color="info" disabled={loading}>
                  {loading ? "Updating..." : "Update FAQ"}
                </ArgonButton>
              </ArgonBox>
            </form>
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditFAQ;
